import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, Copy, Power, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ICONS, ICON_NAMES, iconFor } from "@/lib/navIcons";

const FIELD_TYPES = ["text", "textarea", "number", "date", "select", "toggle", "email", "phone", "url"];
const EMPTY_MODULE = {
  slug: "", name: "", icon: "Cube", description: "", color: "",
  enabled: true, fields: [], list_columns: [], primary_field: null,
};

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

function FieldEditor({ fields, onChange }) {
  const add = () => onChange(fields.concat([{ key: "", label: "", type: "text", required: false, options: [], placeholder: "" }]));
  const upd = (i, patch) => onChange(fields.map((f, idx) => idx === i ? Object.assign({}, f, patch) : f));
  const rm = (i) => onChange(fields.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">FIELDS · {fields.length}</Label>
        <Button size="sm" variant="outline" onClick={add} data-testid="module-add-field">
          <Plus size={12} className="mr-1" />Field
        </Button>
      </div>
      {fields.map((f, i) => (
        <div key={i} className="border rounded-md p-3 space-y-2 bg-secondary/30" data-testid={"module-field-" + i}>
          <div className="grid grid-cols-12 gap-2">
            <Input className="col-span-3 h-8" placeholder="Label" value={f.label}
              onChange={(e) => upd(i, { label: e.target.value, key: f.key || slugify(e.target.value) })} />
            <Input className="col-span-3 h-8 font-mono text-xs" placeholder="key_snake_case" value={f.key}
              onChange={(e) => upd(i, { key: slugify(e.target.value) })} />
            <Select value={f.type} onValueChange={(v) => upd(i, { type: v })}>
              <SelectTrigger className="col-span-2 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="col-span-3 h-8" placeholder="Placeholder" value={f.placeholder}
              onChange={(e) => upd(i, { placeholder: e.target.value })} />
            <button className="col-span-1 text-destructive" onClick={() => rm(i)}><Trash size={14} className="mx-auto" /></button>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={f.required} onCheckedChange={(v) => upd(i, { required: v })} />Required
            </label>
            {f.type === "select" && (
              <Input className="flex-1 h-8" placeholder="Options (comma separated)"
                value={(f.options || []).join(", ")}
                onChange={(e) => upd(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} />
            )}
          </div>
        </div>
      ))}
      {!fields.length && <div className="text-xs text-muted-foreground text-center py-6">No fields yet — click “Field” to add your first.</div>}
    </div>
  );
}

function ModuleDialog({ open, onOpenChange, initial, onSave }) {
  const [f, setF] = useState(EMPTY_MODULE);
  useEffect(() => { setF(initial ? Object.assign({}, EMPTY_MODULE, initial) : EMPTY_MODULE); }, [initial, open]);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    const slug = f.slug || slugify(f.name);
    if (!slug) return toast.error("Slug is required");
    try {
      await onSave(Object.assign({}, f, { slug }));
      onOpenChange(false);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed");
    }
  };
  const Ic = iconFor(f.icon);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="module-dialog">
        <DialogHeader>
          <DialogTitle>{initial && initial.id ? "Edit module" : "New module"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">NAME</Label>
              <Input value={f.name}
                onChange={(e) => setF(Object.assign({}, f, { name: e.target.value, slug: f.slug || slugify(e.target.value) }))}
                data-testid="module-name" placeholder="Projects" />
            </div>
            <div>
              <Label className="text-xs">SLUG (used in URL + permissions)</Label>
              <Input value={f.slug} onChange={(e) => setF(Object.assign({}, f, { slug: slugify(e.target.value) }))}
                data-testid="module-slug" placeholder="projects" className="font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">DESCRIPTION</Label>
            <Textarea rows={2} value={f.description}
              onChange={(e) => setF(Object.assign({}, f, { description: e.target.value }))}
              placeholder="Short internal description of this module." />
          </div>
          <div>
            <Label className="text-xs mb-2 block">ICON — <Ic size={14} className="inline mx-1" weight="duotone" /> {f.icon}</Label>
            <div className="grid grid-cols-12 gap-1 max-h-32 overflow-y-auto p-2 border rounded-md bg-secondary/30">
              {ICON_NAMES.map((name) => {
                const I = ICONS[name];
                const active = f.icon === name;
                return (
                  <button key={name} type="button" onClick={() => setF(Object.assign({}, f, { icon: name }))} title={name}
                    className={"w-7 h-7 rounded flex items-center justify-center " + (active ? "bg-primary text-primary-foreground" : "hover:bg-secondary text-muted-foreground")}>
                    <I size={14} weight="duotone" />
                  </button>
                );
              })}
            </div>
          </div>
          <FieldEditor fields={f.fields} onChange={(fields) => setF(Object.assign({}, f, { fields }))} />
          <div className="flex items-center gap-2 text-xs">
            <Switch checked={f.enabled} onCheckedChange={(v) => setF(Object.assign({}, f, { enabled: v }))} data-testid="module-enabled" />
            Enabled
            <span className="text-muted-foreground ml-4">Permission keys generated: <span className="font-mono">{f.slug || "slug"}.view</span>, <span className="font-mono">{f.slug || "slug"}.create</span>, …</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="module-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DuplicateDialog({ open, onOpenChange, source, onSave }) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  useEffect(() => {
    if (source) { setSlug(source.slug + "_copy"); setName(source.name + " (copy)"); }
  }, [source, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="module-duplicate-dialog">
        <DialogHeader><DialogTitle>Duplicate “{source?.name}”</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label className="text-xs">NEW NAME</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label className="text-xs">NEW SLUG</Label><Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} className="font-mono" data-testid="module-dup-slug" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={async () => {
            try { await onSave({ slug, name }); onOpenChange(false); }
            catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Duplicate failed"); }
          }} data-testid="module-dup-save">Duplicate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ModuleBuilder() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/platform/modules"); setModules(r.data); }
    catch { toast.error("Failed to load modules"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (m) => { setEditing(m); setDialogOpen(true); };
  const openDup = (m) => { setDuplicating(m); setDupOpen(true); };

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/modules/" + editing.id, payload);
      toast.success("Module saved");
    } else {
      await api.post("/platform/modules", payload);
      toast.success("Module created");
    }
    load();
  };

  const duplicate = async (payload) => {
    await api.post("/platform/modules/" + duplicating.id + "/duplicate", payload);
    toast.success("Module duplicated"); load();
  };

  const toggle = async (m) => {
    const r = await api.put("/platform/modules/" + m.id + "/toggle");
    toast.success(r.data.enabled ? "Module enabled" : "Module disabled"); load();
  };

  const remove = async (m) => {
    if (m.is_system) return toast.error("System module — disable it instead");
    const cnt = m.record_count || 0;
    if (cnt) {
      if (!window.confirm(`This module has ${cnt} record(s). Purge them and delete the module?`)) return;
      try { await api.delete("/platform/modules/" + m.id + "?force=true"); }
      catch (e) { return toast.error((e && e.response && e.response.data && e.response.data.detail) || "Delete failed"); }
    } else {
      if (!window.confirm(`Delete module “${m.name}”?`)) return;
      try { await api.delete("/platform/modules/" + m.id); }
      catch (e) { return toast.error((e && e.response && e.response.data && e.response.data.detail) || "Delete failed"); }
    }
    toast.success("Module deleted"); load();
  };

  return (
    <Card className="p-6" data-testid="module-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">Data</div>
          <h3 className="font-heading font-bold text-xl">Module Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Create custom modules with dynamic fields. Add a nav entry via the Navigation Builder pointing to <span className="font-mono">/m/&lt;slug&gt;</span> to expose it in tenant sidebars.</p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="module-add">
          <Plus size={14} className="mr-1.5" />New module
        </Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {modules.map((m) => {
            const Ic = iconFor(m.icon);
            return (
              <div key={m.id} className={"border rounded-lg p-4 space-y-3 hover:bg-secondary/30 " + (m.enabled ? "" : "opacity-60")}
                data-testid={"module-card-" + m.slug}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                      <Ic size={18} weight="duotone" className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-sm flex items-center gap-2">
                        {m.name}
                        {m.is_system && <span className="text-[9px] uppercase tracking-wider text-primary/70 border border-primary/30 px-1 rounded">system</span>}
                        {!m.enabled && <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border px-1 rounded">disabled</span>}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">/m/{m.slug} · {m.fields?.length || 0} fields · {m.record_count || 0} records</div>
                      {m.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{m.description}</div>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={() => toggle(m)} className="p-1.5 rounded hover:bg-secondary" title={m.enabled ? "Disable" : "Enable"} data-testid={"module-toggle-" + m.slug}>
                    <Power size={13} weight="duotone" className={m.enabled ? "text-emerald-600" : "text-muted-foreground"} />
                  </button>
                  <button onClick={() => openDup(m)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground" title="Duplicate" data-testid={"module-duplicate-" + m.slug}>
                    <Copy size={13} weight="duotone" />
                  </button>
                  <button onClick={() => openEdit(m)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"module-edit-" + m.slug}>
                    <PencilSimple size={13} weight="duotone" />
                  </button>
                  {!m.is_system && (
                    <button onClick={() => remove(m)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"module-delete-" + m.slug}>
                      <Trash size={13} weight="duotone" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!modules.length && (
            <div className="col-span-2 py-12 text-center text-muted-foreground text-sm">
              <Wrench size={28} weight="duotone" className="mx-auto mb-2 opacity-50" />
              No modules yet — click “New module” to build your first one.
            </div>
          )}
        </div>
      )}
      <ModuleDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSave={save} />
      <DuplicateDialog open={dupOpen} onOpenChange={setDupOpen} source={duplicating} onSave={duplicate} />
    </Card>
  );
}
